import IdGenerator from './IdGenerator'
import {
    Account,
    Action,
    ClientEventType,
    CommonEventType,
    JsonRpc,
    Message,
    Resolver,
    ServerEventType,
    SourceType,
} from './typings'

class Client {
    win: Window
    parent: Window
    #resolvers: {[key: string]: Resolver}
    #events: {[key: string]: (message: Message) => any}
    rpc: JsonRpc
    constructor(Win?: Window) {
        this.parent = Win || window.parent
        this.win = window
        this.#resolvers = {}
        this.#events = {}
        this.addEventListener(async (message: Message) => {
            const resolve = this.#resolvers[message.id]
            const fn = this.#events[message.type]
            if (resolve) {
                if (message.type === CommonEventType.ERROR) {
                    resolve.reject(message.payload)
                } else {
                    resolve.resolve(message.payload)
                }
            }
            if (fn) {
                await fn(message.payload)
            }
        })

        this.rpc = {
            abi_bin_to_json: this.#requestRPC('abi_bin_to_json'),
            abi_json_to_bin: this.#requestRPC('abi_json_to_bin'),
            /** Raw call to `/v1/chain/get_abi` */
            get_abi: this.#requestRPC('get_abi'),
            /** Raw call to `/v1/chain/get_account` */
            get_account: this.#requestRPC('get_account'),
            /** Raw call to `/v1/chain/get_accounts_by_authorizers` */
            get_accounts_by_authorizers: this.#requestRPC('get_accounts_by_authorizers'),
            /** Raw call to `get_activated_protocol_features` */
            get_activated_protocol_features: this.#requestRPC('get_activated_protocol_features'),
            /** Raw call to `/v1/chain/get_block_header_state` */
            get_block_header_state: this.#requestRPC('get_block_header_state'),
            /** Raw call to `/v1/chain/get_block_info` */
            get_block_info: this.#requestRPC('get_block_info'),
            /** Raw call to `/v1/chain/get_block` */
            get_block: this.#requestRPC('get_block'),
            /** Raw call to `/v1/chain/get_code` */
            get_code: this.#requestRPC('get_code'),
            /** Raw call to `/v1/chain/get_code_hash` */
            get_code_hash: this.#requestRPC('get_code_hash'),
            /** Raw call to `/v1/chain/get_currency_balance` */
            get_currency_balance: this.#requestRPC('get_currency_balance'),
            /** Raw call to `/v1/chain/get_currency_stats` */
            get_currency_stats: this.#requestRPC('get_currency_stats'),
            /** Raw call to `/v1/chain/get_info` */
            get_info: this.#requestRPC('get_info'),
            /** Raw call to `/v1/chain/get_producer_schedule` */
            get_producer_schedule: this.#requestRPC('get_producer_schedule'),
            /** Raw call to `/v1/chain/get_producers` */
            get_producers: this.#requestRPC('get_producers'),
            /** Raw call to `/v1/chain/get_raw_code_and_abi` */
            get_raw_code_and_abi: this.#requestRPC('get_raw_code_and_abi'),
            /** calls `/v1/chain/get_raw_code_and_abi` and pulls out unneeded raw wasm code */
            getRawAbi: this.#requestRPC('getRawAbi'),
            /** Raw call to `/v1/chain/get_raw_abi` */
            get_raw_abi: this.#requestRPC('get_raw_abi'),
            /** Raw call to `/v1/chain/get_scheduled_transactions` */
            get_scheduled_transactions: this.#requestRPC('get_scheduled_transactions'),
            /** Raw call to `/v1/chain/get_table_rows` */
            get_table_rows: this.#requestRPC('get_table_rows'),
            /** Raw call to `/v1/chain/get_kv_table_rows` */
            get_kv_table_rows: this.#requestRPC('get_kv_table_rows'),
            /** Raw call to `/v1/chain/get_table_by_scope` */
            get_table_by_scope: this.#requestRPC('get_table_by_scope'),
            /** Get subset of `availableKeys` needed to meet authorities in `transaction`. Implements `AuthorityProvider` */
            getRequiredKeys: this.#requestRPC('getRequiredKeys'),
            /** Push a serialized transaction (replaced by send_transaction, but returned format has changed) */
            push_transaction: this.#requestRPC('push_transaction'),
            /** Raw call to `/v1/chain/push_ro_transaction */
            push_ro_transaction: this.#requestRPC('push_ro_transaction'),
            push_transactions: this.#requestRPC('push_transactions'),
            /** Send a serialized transaction */
            send_transaction: this.#requestRPC('send_transaction'),
            /** Raw call to `/v1/db_size/get` */
            db_size_get: this.#requestRPC('db_size_get'),
            /** Raw call to `/v1/trace_api/get_block` */
            trace_get_block: this.#requestRPC('trace_get_block'),
            /** Raw call to `/v1/history/get_actions` */
            history_get_actions: this.#requestRPC('history_get_actions'),
            /** Raw call to `/v1/history/get_transaction` */
            history_get_transaction: this.#requestRPC('history_get_transaction'),
            /** Raw call to `/v1/history/get_key_accounts` */
            history_get_key_accounts: this.#requestRPC('history_get_key_accounts'),
            /** Raw call to `/v1/history/get_controlled_accounts` */
            history_get_controlled_accounts: this.#requestRPC('history_get_controlled_accounts'),
        }
    }

    #requestRPC(fnName: string): any {
        return async (...args) => {
            return await this.request({
                method: 'rpc',
                params: {
                    request: {
                        method: fnName,
                        params: args,
                    },
                },
            })
        }
    }
    async request(params: {method: string; params?: any}): Promise<any[]> {
        return await this.postMessage({
            type: ClientEventType.REQUEST,
            payload: params,
        } as Message)
    }
    async getAccount(): Promise<Account> {
        return await this.postMessage({
            type: ClientEventType.GET_ACCOUNT,
        } as Message)
    }
    async transact(actions: Action[]): Promise<any> {
        return await this.postMessage({
            type: ClientEventType.TRANSACT,
            payload: actions,
        } as Message)
    }
    async signMessage(msg: string): Promise<string> {
        return await this.postMessage({
            type: ClientEventType.SIGN_MESSAGE,
            payload: msg,
        } as Message)
    }
    on(type: ServerEventType, fn: (payload: any) => any) {
        this.#events[type] = fn
    }
    addEventListener(listener: (msg: Message) => any) {
        this.win.addEventListener('message', (e) => {
            const msg = e.data as Message
            if (msg.source === SourceType.AMAX_UP_SERVER) {
                listener(msg)
            }
        })
    }
    postMessage(message: Message): Promise<any> {
        return new Promise((resolve, reject) => {
            const id = IdGenerator.numeric(24)
            message.id = id
            message.source = SourceType.AMAX_UP_CLIENT
            this.#resolvers[id] = {resolve, reject}
            this.parent.postMessage(message, '*')
        })
    }
    isInIframe() {
        return window.parent !== window
    }
}

export default Client
